/* eslint-disable @typescript-eslint/restrict-template-expressions */
import UrlRule from '../editor/utils/UrlRule';
import type { MentionType } from '../graphql/generated/graphql';
import safeParseUrl from '../utils/url/safeParseUrl';

const GITHUB_ID_REGEX = /^([^/]+)\/([^#]+)#(.+)$/;
const GITLAB_ISSUE_ID_REGEX = /^([^/]+)\/([^#]+)#(.+)$/;
const GITLAB_MERGE_ID_REGEX = /^([^/]+)\/([^#]+)!(.+)$/;
const HEIGHT_TASK_ID_REGEX = /^(?:([\w-]+)\/|([\w-]+)#)(\d+)$/;

const ASANA_REGEXES = [
  /^(?:https?:\/\/)?app\.asana\.com\/0\/(?:(inbox|home)\/)?(?<projectId>\d+)\/(?<taskId>\d+)(?:\/\d+)?(?:\/f)?\/?$/,
  /^(?:https?:\/\/)?app\.asana\.com\/1\/(?<workspaceId>\d+)\/project\/(?<projectId>\d+)\/task\/(?<taskId>\d+)/,
];
const CLICKUP_CUSTOM_ID_REGEX =
  /^(?:https?:\/\/)?app\.clickup\.com\/t\/(\d+)\/([\w-]+)\/?$/;
const CLICKUP_DEFAULT_ID_REGEX =
  /^(?:https?:\/\/)?app\.clickup\.com\/t\/(\w+)\/?$/;
const SHORTCUT_EPIC_REGEX =
  /^(?:https?:\/\/)?app\.(?:shortcut.com|clubhouse.io)\/([\w-]+)\/epic\/(\d+)(?:\/[\w-]+)?\/?$/;
const SHORTCUT_STORY_REGEX =
  /^(?:https?:\/\/)?app\.(?:shortcut.com|clubhouse.io)\/([\w-]+)\/story\/(\d+)(?:\/[\w-]+)?\/?$/;
const GITHUB_REGEX =
  /^(?:https?:\/\/)?github.com\/([^/]+)\/([^/]+)\/(?:issues|pull)\/(\d+)\/?$/;
const GITLAB_ISSUE_REGEX =
  /^(?:https?:\/\/)?gitlab.com\/(.+)\/((?!-)[^/]+)(?:\/-)?\/(?:issues)\/(\d+)\/?$/;
const GITLAB_MERGE_REGEX =
  /^(?:https?:\/\/)?gitlab.com\/(.+)\/((?!-)[^/]+)(?:\/-)?\/(?:merge_requests)\/(\d+)\/?$/;
const GURU_CARD_REGEX =
  /^(?:https?:\/\/)?app\.getguru\.com\/card\/([\w-]+)(?:\/[\w-]+)?\/?$/;
const HEIGHT_TASK_SUBDOMAIN_REGEX =
  /^(?:https?:\/\/)?([\w-]+)\.height\.app(?:\/user)?(?:\/[\w-]+)?\/T-(\d+)\/?$/;
const HEIGHT_TASK_WORKSPACE_REGEX =
  /^(?:https?:\/\/)?height\.app\/([\w-]+)(?:\/user)?(?:\/[\w-]+)?\/T-(\d+)\/?$/;
const JIRA_REGEXES = [
  /^(?:https?:\/\/)?(.+)\.atlassian\.net\/browse\/([^?]+)(?:\?.*)?$/,
  /^(?:https?:\/\/)?(.+)\.atlassian\.net\/secure\/RapidBoard.jspa\?.*selectedIssue=([^&]+)/,
  /^(?:https?:\/\/)?(.+)\.atlassian\.net\/projects\/(?:[^/]+)\/issues\/([^?/]+)/,
];
const FRESHDESK_REGEX =
  /^(?:https?:\/\/)?([\w-]+)\.freshdesk\.com\/a\/tickets\/([\d]+)\/?$/;
const LINEAR_ISSUE_REGEX =
  /^(?:https?:\/\/)?linear\.app\/([\w-]+)\/issue\/([\w-]+)(?:\/.+)?\/?$/;
const MONDAY_BOARD_REGEX =
  /^(?:https?:\/\/)?([\w-]+)\.monday\.com\/boards\/(\d+)\/?$/;
const MONDAY_PULSE_REGEX =
  /^(?:https?:\/\/)?([\w-]+)\.monday\.com\/boards\/(\d+)\/pulses\/(\d+)\/?$/;
const TODOIST_TASK_REGEX =
  /^(?:https?:\/\/)?(?:app\.)?todoist\.com\/(?:app\/[\w\d/-]*task\/|showTask\?id=)(?:[a-z0-9-]*?)([a-zA-Z0-9]+)\/?$/;
const TRELLO_CARD_REGEX =
  /^(?:https?:\/\/)?trello\.com\/c\/(\w+)(?:\/[\w-%]+)?\/?#?$/;
const WRIKE_WORKSPACE_REGEX =
  /^(?:https?:\/\/)?(?:[\w-]+\.)?wrike.com\/workspace.htm\?.+#.*t=(\d+).*$/;
const WRIKE_PERMALINK_REGEX =
  /^(?:https?:\/\/)?(?:[\w-]+\.)?wrike.com\/open.htm\?id=(\d+)$/;
const ZENDESK_REGEX =
  /^(?:https?:\/\/)?([\w-]+)\.zendesk.com\/agent\/tickets\/(\d+)\/?$/;

const generateSimpleParseUrl =
  (regex: RegExp) =>
  (url: string): ParseUrlResult => {
    const match = url.match(regex);
    return match ? { id: match.slice(1).join('/') } : false;
  };

const generateUrlRuleParser =
  (
    urlRule: UrlRule,
    toIdSegments: (matched: Record<string, string>) => ParseUrlResult,
  ) =>
  (url: string): ParseUrlResult => {
    const parsedUrl = safeParseUrl(url);
    const matched = parsedUrl && urlRule.match(parsedUrl);
    if (!matched) return false;
    return toIdSegments(matched);
  };

type KebabCase<T extends string> = T extends `${infer L}_${infer R}`
  ? `${KebabCase<L>}-${KebabCase<R>}`
  : Lowercase<T>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type ObjectName<T extends string> = T extends `${infer _}_${infer R}`
  ? ObjectName<R>
  : Lowercase<T>;

export type MentionMode = 'embed' | 'mention';

type ParseUrlResult = false | { id: string; params?: Record<string, string> };

export interface MentionBehavior<T extends string> {
  mentionType: KebabCase<T>;
  objectName: ObjectName<T>;
  buildUrl: (id: string, params?: Record<string, string>) => string;
  parseUrl: (url: string) => ParseUrlResult;
  modes: [MentionMode, ...MentionMode[]];
}

interface SearchBehavior {
  searchResultType: string;
  icon?: { brand: string; product: string };
  platform?: string;
}

export const Behaviors = {
  Mention: [
    'ASANA_TASK',
    'CLICKUP_TASK',
    'SHORTCUT_EPIC',
    'SHORTCUT_STORY',
    'FRESHDESK_TICKET',
    'GITHUB_ISSUE',
    'GITLAB_ISSUE',
    'GITLAB_MERGE',
    'GURU_CARD',
    'HEIGHT_TASK',
    'JIRA_ISSUE',
    'LINEAR_ISSUE',
    'MONDAY_BOARD',
    'MONDAY_PULSE',
    'QUIP_THREAD',
    'TODOIST_TASK',
    'TRELLO_CARD',
    'WRIKE_TASK',
    'ZENDESK_ARTICLE',
    'ZENDESK_TICKET',
  ],
  Search: [
    'GITHUB_PR',
    'GOOGLE_DRIVE_AUDIO',
    'GOOGLE_DRIVE_DOC',
    'GOOGLE_DRIVE_IMAGE',
    'GOOGLE_DRIVE_OTHER',
    'GOOGLE_DRIVE_SHEET',
    'GOOGLE_DRIVE_SLIDE',
    'GOOGLE_DRIVE_VIDEO',
    'SLACK_CHANNEL_MESSAGE',
    'SLACK_DIRECT_MESSAGE',
    'SLACK_GROUP_MESSAGE',
    'SLACK_OTHER',
    'ZENDESK_ARTICLE',
    'ZENDESK_TICKET',
  ],
} as const;

type Mentions = {
  [name in (typeof Behaviors.Mention)[number]]: MentionBehavior<name>;
};

type Searches = {
  [name in (typeof Behaviors.Search)[number]]: SearchBehavior;
};

const INTEGRATION_MENTIONS: Mentions &
  Searches & {
    [key in Exclude<MentionType, keyof Mentions | keyof Searches>]?: undefined;
  } = {
  ASANA_TASK: {
    mentionType: 'asana-task',
    objectName: 'task',
    modes: ['mention'],
    buildUrl: (id, params) => {
      const projectId = params?.projectId;
      const workspaceId = params?.workspaceId;

      // This is for backward compatibility for when we didn't store the projectId
      if (typeof projectId !== 'string') {
        return `https://app.asana.com/0/0/${id}`;
      }

      if (typeof workspaceId === 'string') {
        return `https://app.asana.com/1/${workspaceId}/project/${projectId}/task/${id}`;
      }

      return `https://app.asana.com/0/${projectId}/${id}`;
    },
    parseUrl(url) {
      for (const regex of ASANA_REGEXES) {
        if (regex.test(url)) {
          const customMatch = url.match(regex);
          const { workspaceId, projectId, taskId } = customMatch?.groups ?? {};
          if (projectId && taskId) {
            return {
              id: taskId,
              params: { projectId, ...(workspaceId ? { workspaceId } : {}) },
            };
          }
        }
      }

      return false;
    },
  },
  CLICKUP_TASK: {
    mentionType: 'clickup-task',
    objectName: 'task',
    modes: ['mention'],
    buildUrl: (id) => `https://app.clickup.com/t/${id}`,
    parseUrl: (url) => {
      const defaultMatch = url.match(CLICKUP_DEFAULT_ID_REGEX);
      if (defaultMatch) return { id: defaultMatch[1]! };

      const customMatch = url.match(CLICKUP_CUSTOM_ID_REGEX);
      if (customMatch) {
        const [, teamId, customTaskId] = customMatch;
        return { id: `${teamId}/${customTaskId}` };
      }

      return false;
    },
  },
  SHORTCUT_EPIC: {
    mentionType: 'shortcut-epic',
    objectName: 'epic',
    modes: ['mention'],
    buildUrl: (id) => {
      const [team, number] = id.split('/');
      return `https://app.shortcut.com/${team}/epic/${number}/`;
    },
    parseUrl: generateSimpleParseUrl(SHORTCUT_EPIC_REGEX),
  },
  SHORTCUT_STORY: {
    mentionType: 'shortcut-story',
    objectName: 'story',
    modes: ['mention'],
    buildUrl: (id) => {
      const [team, number] = id.split('/');
      return `https://app.shortcut.com/${team}/story/${number}/`;
    },
    parseUrl: generateSimpleParseUrl(SHORTCUT_STORY_REGEX),
  },
  FRESHDESK_TICKET: {
    mentionType: 'freshdesk-ticket',
    objectName: 'ticket',
    modes: ['mention'],
    buildUrl: (id) => {
      const [subdomain, number] = id.split('/');
      return `https://${subdomain}.freshdesk.com/a/tickets/${number}`;
    },
    parseUrl: generateSimpleParseUrl(FRESHDESK_REGEX),
  },
  GITHUB_ISSUE: {
    mentionType: 'github-issue',
    objectName: 'issue',
    modes: ['mention'],
    buildUrl: (id) => {
      const [owner, repo, number] = (id.match(GITHUB_ID_REGEX) ?? []).slice(1);
      return `https://github.com/${owner}/${repo}/issues/${number}`;
    },
    parseUrl: (url) => {
      const match = url.match(GITHUB_REGEX);
      if (match) {
        const [, owner, repo, number] = match;
        return { id: `${owner}/${repo}#${number}` };
      }

      return false;
    },
  },
  GITHUB_PR: {
    searchResultType: 'Pull Request',
  },
  GITLAB_ISSUE: {
    mentionType: 'gitlab-issue',
    objectName: 'issue',
    modes: ['mention'],
    buildUrl: (id) => {
      const [owner, repo, number] = (
        id.match(GITLAB_ISSUE_ID_REGEX) ?? []
      ).slice(1);
      return `https://gitlab.com/${owner}/${repo}/issues/${number}`;
    },
    parseUrl: (url) => {
      const match = url.match(GITLAB_ISSUE_REGEX);
      if (match) {
        const [, owner, repo, number] = match;
        return { id: `${owner}/${repo}#${number}` };
      }

      return false;
    },
  },
  GITLAB_MERGE: {
    mentionType: 'gitlab-merge',
    objectName: 'merge',
    modes: ['mention'],
    buildUrl: (id) => {
      const [owner, repo, number] = (
        id.match(GITLAB_MERGE_ID_REGEX) ?? []
      ).slice(1);
      return `https://gitlab.com/${owner}/${repo}/-/merge_requests/${number}`;
    },
    parseUrl: (url) => {
      const match = url.match(GITLAB_MERGE_REGEX);
      if (match) {
        const [, owner, repo, number] = match;
        return { id: `${owner}/${repo}!${number}` };
      }

      return false;
    },
  },
  GOOGLE_DRIVE_AUDIO: {
    platform: 'google-drive',
    searchResultType: 'Audio',
  },
  GOOGLE_DRIVE_DOC: {
    platform: 'google-drive',
    searchResultType: 'Doc',
  },
  GOOGLE_DRIVE_IMAGE: {
    platform: 'google-drive',
    searchResultType: 'Image',
  },
  GOOGLE_DRIVE_OTHER: {
    platform: 'google-drive',
    searchResultType: 'Other',
  },
  GOOGLE_DRIVE_SHEET: {
    platform: 'google-drive',
    searchResultType: 'Sheet',
  },
  GOOGLE_DRIVE_SLIDE: {
    platform: 'google-drive',
    searchResultType: 'Slide',
  },
  GOOGLE_DRIVE_VIDEO: {
    platform: 'google-drive',
    searchResultType: 'Video',
  },
  GURU_CARD: {
    mentionType: 'guru-card',
    objectName: 'card',
    modes: ['mention'],
    buildUrl: (id) => `https://app.getguru.com/card/${id}`,
    parseUrl: generateSimpleParseUrl(GURU_CARD_REGEX),
  },
  HEIGHT_TASK: {
    mentionType: 'height-task',
    objectName: 'task',
    modes: ['mention'],
    buildUrl: (id) => {
      const [workspace, subdomain, index] = (
        id.match(HEIGHT_TASK_ID_REGEX) ?? []
      ).slice(1);

      if (subdomain) return `https://${subdomain}.height.app/T-${index}`;
      return `https://height.app/${workspace}/T-${index}`;
    },
    parseUrl: (url) => {
      const subdomainMatch = url.match(HEIGHT_TASK_SUBDOMAIN_REGEX);

      if (subdomainMatch) {
        const [, subdomain, index] = subdomainMatch;
        return { id: `${subdomain}#${index}` };
      }

      const workspaceMatch = url.match(HEIGHT_TASK_WORKSPACE_REGEX);
      if (workspaceMatch) {
        const [, workspace, index] = workspaceMatch;
        return { id: `${workspace}/${index}` };
      }

      return false;
    },
  },
  JIRA_ISSUE: {
    mentionType: 'jira-issue',
    objectName: 'issue',
    modes: ['mention'],
    buildUrl: (id) => {
      const [subdomain, key] = id.split('#');
      return `https://${subdomain}.atlassian.net/browse/${key}`;
    },
    parseUrl: (url) => {
      for (const regex of JIRA_REGEXES) {
        if (regex.test(url)) {
          const match = url.match(regex);
          if (match) {
            const [, subdomain, issueId] = match;
            return { id: `${subdomain}#${issueId}` };
          }
        }
      }

      return false;
    },
  },
  LINEAR_ISSUE: {
    mentionType: 'linear-issue',
    objectName: 'issue',
    modes: ['mention'],
    buildUrl: (id) => {
      const [namespace, key] = id.split('/');
      return `https://linear.app/${namespace}/issue/${key}`;
    },
    parseUrl: generateSimpleParseUrl(LINEAR_ISSUE_REGEX),
  },
  MONDAY_BOARD: {
    mentionType: 'monday-board',
    objectName: 'board',
    modes: ['mention'],
    buildUrl: (id) => {
      const [subdomain, board] = id.split('/');
      return `https://${subdomain}.monday.com/boards/${board}`;
    },
    parseUrl: generateSimpleParseUrl(MONDAY_BOARD_REGEX),
  },
  MONDAY_PULSE: {
    mentionType: 'monday-pulse',
    objectName: 'pulse',
    modes: ['mention'],
    buildUrl: (id) => {
      const [subdomain, board, pulse] = id.split('/');
      return `https://${subdomain}.monday.com/boards/${board}/pulses/${pulse}`;
    },
    parseUrl: generateSimpleParseUrl(MONDAY_PULSE_REGEX),
  },
  QUIP_THREAD: {
    mentionType: 'quip-thread',
    objectName: 'thread',
    modes: ['embed', 'mention'],
    buildUrl: (id) => {
      const [domain, documentId] = id.split('/');
      return `https://${domain}.quip.com/${documentId}`;
    },
    parseUrl: generateUrlRuleParser(
      new UrlRule({
        host: ':domain.quip.com',
        path: '/:id(\\w{12})',
      }),
      ({ domain, id }) =>
        domain && id ? { id: [domain, id].join('/') } : false,
    ),
  },
  SLACK_CHANNEL_MESSAGE: {
    searchResultType: 'Channel Message',
  },
  SLACK_DIRECT_MESSAGE: {
    searchResultType: 'Direct Message',
  },
  SLACK_GROUP_MESSAGE: {
    searchResultType: 'Group Message',
  },
  SLACK_OTHER: {
    searchResultType: 'Other',
  },
  TODOIST_TASK: {
    mentionType: 'todoist-task',
    objectName: 'task',
    modes: ['mention'],
    buildUrl: (id) => `https://todoist.com/app/task/${id}`,
    parseUrl: generateSimpleParseUrl(TODOIST_TASK_REGEX),
  },
  TRELLO_CARD: {
    mentionType: 'trello-card',
    objectName: 'card',
    modes: ['mention'],
    buildUrl: (id) => `https://trello.com/c/${id}`,
    parseUrl: generateSimpleParseUrl(TRELLO_CARD_REGEX),
  },
  WRIKE_TASK: {
    mentionType: 'wrike-task',
    objectName: 'task',
    modes: ['mention'],
    buildUrl: (id) => `https://www.wrike.com/open.htm?id=${id}`,
    parseUrl: (url) => {
      const workspaceMatch = url.match(WRIKE_WORKSPACE_REGEX);
      if (workspaceMatch) return { id: workspaceMatch[1]! };

      const permalinkMatch = url.match(WRIKE_PERMALINK_REGEX);
      if (permalinkMatch) return { id: permalinkMatch[1]! };

      return false;
    },
  },
  ZENDESK_ARTICLE: {
    icon: { brand: 'zendesk', product: 'guide' },
    searchResultType: 'Article',
    mentionType: 'zendesk-article',
    objectName: 'article',
    modes: ['embed', 'mention'],
    buildUrl: (id) => {
      const [domain, articleId] = id.split('#');
      return `https://${domain}.zendesk.com/hc/articles/${articleId}`;
    },
    parseUrl: generateUrlRuleParser(
      new UrlRule({
        host: ':domain.zendesk.com',
        path: '/hc/:locale?/articles/:id',
      }),
      ({ domain, id }) => {
        if (domain && id) {
          const articleId = id.split('-')[0];
          if (articleId) {
            return { id: `${domain}#${articleId}` };
          }
        }
        return false;
      },
    ),
  },
  ZENDESK_TICKET: {
    icon: { brand: 'zendesk', product: 'support' },
    searchResultType: 'Ticket',
    mentionType: 'zendesk-ticket',
    objectName: 'ticket',
    modes: ['mention'],
    buildUrl: (id) => {
      const [subdomain, number] = id.split('#');
      return `https://${subdomain}.zendesk.com/agent/tickets/${number}`;
    },
    parseUrl: (url) => {
      const match = url.match(ZENDESK_REGEX);
      if (match) {
        const [, subdomain, id] = match;
        return { id: `${subdomain}#${id}` };
      }

      return false;
    },
  },
};

export default INTEGRATION_MENTIONS;
